<template lang="pug">
.theindex-post(v-if="additional")
  hero(
    v-if="additional.headerPics"
    :image="additional.headerPics.hero"
    :headline="post.title.rendered"
  )
  .main-container-small.date-container
    .author {{author}}
    .date
      span.published(v-if="post.date") {{ `${__('Published')}: ${publishedDate}` }}
      span(v-if="showModifiedDate") &nbsp;&vert;&nbsp;
      span.modified(v-if="showModifiedDate") {{ `${__('Updated')}: ${modifiedDate}` }}
  .event-info
    .event-info-container.main-container-small
      .l
        img(:src="additional.eventHighlightPhoto" alt="")
      .r
        h1(v-html="post.title.rendered" v-translate="THE_INDEX_TITLES_NS")
        .location
          .l
            img(:src="additional.headerPics.logo" alt="")
          .r
            .event-date {{ additional.eventDate }}
            .event-location {{ additional.location }}
        .event-message
          p(v-for="line in additional.eventMessage" v-html="line" v-translate="`theindex_${post.id}`")
        RogueButton(v-if="galleryPost && additional.galleryId" :link="`/theindex/gallery/${galleryPost}`" color="red") View Full Photo Gallery
  .event-results(:style="'background-image: url(' + additional.eventResults.backgroundImg + ')'" v-if='additional.eventResults.resultTables')
    .faded-background(v-for='(eventResult, k) in additional.eventResults.resultTables' :key='"results-table-" + k')
      h2.bold-section-header(v-if='k === 0') Event Results
      h3(v-if='eventResult.title && eventResult.title.length > 0') {{ eventResult.title }}
      .results-table-wrapper
        Carousel.event-results-table-mobile.stats-table(:id="'event-results-table-mobile-' + k")
          .table-row(v-for='(results, i) in eventResult.athleteResults' :key='"results-slide-"+i')
            .event(v-for='(result, j) in results' :key="'table-result-' + j")
              p.event-header {{ eventResult.headers[j] }}
              p.event-result(:class='results[j]') {{ results[j] }}
        .event-results-table-desktop.stats-table
          .table-row.header-row
            .cell.header-cell(v-for='(header, i) in eventResult.headers' :key="'header-' + i" :style="'flex-basis: calc(100% / ' + eventResult.headers.length + ')'")
              p.header-text {{ header }}
          .table-row.data-row(v-for='(athlete, i) in eventResult.athleteResults' :key="'athlete-' + i")
            .cell.data-cell(v-for='(result, i) in athlete' :key="'result-' + i" :style="'width: calc(100% / ' + eventResult.headers.length + ')'")
              p.result-text(:class='result') {{ result }}
  .related-posts.main-container
    PostsGrid( :query="{ tags: additional.relatedContentSectionTagID }" :show-filters="false")
  .athletes(v-if="additional.otherAthletes && additional.otherAthletes.length")
    h2 {{ __('Athletes') }}
    .athletes-conatiner.main-container
      .athlete(
        v-for="athlete in additional.otherAthletes"
        :key="`athlete${athlete.name}`"
      )
        StaticImage.athlete-image(
          :src="athlete.picture"
          alt=""
          :width="150"
          :height="150"
        )
        .athlete-name {{ athlete.name }}
  .bottom-content(v-if="relatedTag")
    h2 {{ additional.bottomContent.title }}
    .bottom-content-container.main-container
      .quote {{ additional.bottomContent.quote }}
      PostsGrid( :query="{tags: relatedTag}" :show-filters="false" :limit="3")
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue'
import { THE_INDEX_TITLES_NS } from '../constants'
import hero from '@/js/pages/theindex/components/hero.vue'
import { loadPosts } from '@/js/pages/theindex/helpers/search'
import { searchTags } from '@/js/pages/theindex/helpers/tags'
import PostsGrid from '@/js/pages/theindex/components/posts-grid.vue'
import { useRogue } from '@/js/services/Rogue'
import { formatDate } from '@/js/helpers/date'

const props = defineProps({
  post: {
    type     : Object,
    required : true,
  },
  authorList: {
    type    : Array,
    default : () => ([]),
  },
})

const rogue = useRogue()

const additional = ref<any>(null)
const galleryPost = ref<any>(null)
const relatedTag = ref<number | null>(null)

const author = computed<string | null>(() => {
  const name = props.post._embedded?.author?.[0]?.name
  if (!name || !props.authorList.length) return null
  const lower = name.toLowerCase()

  return props.authorList.some(auth => lower.includes(auth)) ? name : null
})
const publishedDate = computed<string>(() => formatDate(props.post.date, rogue))
const modifiedDate = computed<string>(() => formatDate(props.post.modified, rogue))
const showModifiedDate = computed<boolean>(() => publishedDate.value !== modifiedDate.value)


async function load() {
  if (props.post.customJs) {
    additional.value = JSON.parse(props.post.customJs)
    if (additional.value.galleryId) {
      loadPosts(rogue, { include: additional.value.galleryId }).then(posts => galleryPost.value = posts[0]?.slug)
    }
    const [tag] = await searchTags(rogue, additional.value.relatedContentSearchWord)
    if (tag) {
      relatedTag.value = tag.id
    }
  }
}

watch(() => props.post, load)

onMounted(() => {
  load()
})
</script>

<style lang="scss" scoped>
.event-info-container, .location {
  display: flex;
  justify-content: space-between;
}

h2 {
  text-align: center;
  font-size: $size-xxxl;
}

.event-info {
  padding-top: $space-m;
  padding-bottom: $space-m;
  margin-bottom: $space-m;
  background-color: $color-gray-x-light;

  .l {
    flex-basis: calc(40% - #{$space-s});
  }

  .r {
    flex-basis: calc(60% - #{$space-s});
  }

  p {
    font-size: $size-s;
    line-height: 1.5;
  }
}

.location {
  font-family: $font-condensed;
  align-items: center;
  margin-bottom: $space-s;

  .event-date {
    font-weight: bold;
  }
}

.athletes {
  padding-top: $space-m;
  padding-bottom: $space-m;
  margin-bottom: $space-m;
  background-color: $color-gray-x-light;

  .athletes-conatiner {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }

  .athlete {
    width: calc(150px + #{$space-xxs} + #{$space-xxs});
    padding: $space-xxs;
  }

  .athlete-image {
    box-shadow: 0px 1px 5px -1px rgba(0, 0, 0, .75)
  }

  .athlete-name {
    margin-top: 10px;
    text-align: center;
    line-height: 1.3;
  }
}

.event-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-position: center;
  background-size: cover;
  position: relative;
  margin-bottom: 60px;
}

.faded-background {
  padding: 35px 20px;
  width: 100%;
  background: rgba(0,0,0,0.7);

  h2 {
    color: $color-white;
    margin-bottom: 30px;
  }

  h3 {
    color: $color-white;
    text-transform: uppercase;
    margin-bottom: 20px;
    font-size: 20px;
  }
}

.results-table-wrapper {
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: rgba(51,51,51,0.7);

  @include mobile() {
    max-width: 300px;
  }
}

.event-results-table-mobile {
  display: none;
  overscroll-behavior: contain;

  @include mobile() {
    display: flex;
    flex-direction: column;

    .table-row {
      flex-direction: column;
      padding: 10px 81px;

      p {
        color: white;
        opacity: 1;
        text-align: center;

        &.header-text {
          text-transform: uppercase;
          font-weight: bold;
        }

        &.event-result {
          margin-bottom: 20px;
        }

        &.event-header {
          text-transform: uppercase;
          font-weight: bold;
          margin-bottom: 0px;
        }
      }
    }
  }
}

.event-results-table-desktop {
  display: flex;
  flex-direction: column;

  @include mobile() {
    display: none;
    flex-direction: column;

    .table-row {
      flex-direction: row;
    }
  }
}

.table-row {
  display: flex;
  flex-direction: row;

  .cell {
    p {
      color: white;
      opacity: 1;
      text-align: center;

      &.header-text {
        text-transform: uppercase;
        font-weight: bold;
      }
    }
  }
}

.bottom-content {
  .quote {
    font-style: italic;
    font-size: $size-xl;
    color: $color-gray-dark;
    margin-bottom: 30px;
  }
}

.date-container{
  margin-bottom: $size-m;
}

.date{
  font-size: $size-xs;
  color: $color-gray-x-dark;
  text-align: justify;

  .published, .modified {
    font-style: italic;    
  }
}
.author{
  text-align: justify;
  font-style: italic;
}
</style>
